import type { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form } from "@ui/Form";
import { MultiStepFormProvider } from "@/providers";
import { useIsMobile, useMultiStepForm } from "@/hooks";
import { getFormSteps } from "@/utils/formConfig";
import { CalculationInfoCard, FullAnalysisCard, InfoCard } from "@/components/Cards";
import FormStepper from "@/components/FormStepper";
import { FormContent } from "./FormContent";

export const Home: FC<{ testInitialStep?: number }> = ({ testInitialStep }) => {
  const { t } = useTranslation();

  return (
    <MultiStepFormProvider steps={getFormSteps(t)} testInitialStep={testInitialStep}>
      <HomeContent />
    </MultiStepFormProvider>
  );
};

const HomeContent: FC = () => {
  const isMobile = useIsMobile();
  const { submit, form, isLastStep } = useMultiStepForm();
  const { t } = useTranslation();

  const Card = isLastStep ? ResultsInfoCard : CalculationInfoCard;

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(submit)}
          data-test="multi-step-form"
          className="flex flex-col gap-8"
        >
          <div className="space-y-4 sm:space-y-8">
            <h1 className="text-xl font-bold sm:text-2xl lg:text-[34px]" data-test="title">
              {t("checkBestSolution")}
            </h1>
            {isMobile && <Card className="flex-1 lg:hidden" />}
          </div>

          <div className="flex flex-col gap-8 lg:flex-row">
            <FormStepper />
            {!isMobile && (
              <div className="flex-1 flex-col gap-8 lg:flex lg:px-4">
                <Card />
                <FormContent />
                {isLastStep && <FullAnalysisCard />}
              </div>
            )}
          </div>
          {isMobile && <FormContent />}
          {isLastStep && <FullAnalysisCard className="lg:hidden" />}
        </form>
      </Form>
    </FormProvider>
  );
};

const ResultsInfoCard: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  return <InfoCard className={className} text={t("resultsCard")} />;
};
